import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, KeyRound, ArrowUpCircle, FolderOpen, RefreshCw, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAiAccessStore } from '@/store/aiAccessStore'
import { useDockUiStore } from '@/store/dockUiStore'
import { quickSetupLlm, saveSettings } from '@/api/settings'
import { formatCredits } from '@/api/billing'
import OutOfCreditsOptions from './OutOfCreditsOptions'

type Provider = 'openai' | 'anthropic' | 'google' | 'deepseek'

const PROVIDERS: { value: Provider; label: string }[] = [
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'google', label: 'Google (Gemini)' },
  { value: 'deepseek', label: 'DeepSeek' },
]

function fmt(n?: number | null) {
  if (n == null) return '—'
  return n.toLocaleString()
}

/** UTC-midnight reset shown in the user's own clock ("5:30 AM"), with the
 *  day when it is not today locally. */
function formatResetTime(iso: string) {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  const time = at.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
  return at.toDateString() === new Date().toDateString()
    ? time
    : `${time} ${at.toLocaleDateString([], { weekday: 'short' })}`
}

/**
 * Global, graceful handler for AI-access (402) errors. Mounted once in
 * AppLayout. Out of credits (or of a monthly allowance) it offers the three
 * ways forward — buy credits, use the user's own AI key, switch to a free
 * model — and then CARRIES ON the refused work: an HTTP refusal is replayed
 * (`retry`), an agent-socket refusal is resent by whichever dock mounts next
 * (`resend` → `pendingResend`), since a fix that switched AI engines replaces
 * the dock that raised it.
 */
export default function AiAccessModal() {
  const navigate = useNavigate()
  const { error, reset, setPendingResend } = useAiAccessStore()
  const setProjectModalOpen = useDockUiStore((st) => st.setProjectModalOpen)
  const [mode, setMode] = useState<'choose' | 'key'>('choose')
  const [provider, setProvider] = useState<Provider>('anthropic')
  const [apiKey, setApiKey] = useState('')
  const [saving, setSaving] = useState(false)

  if (!error) return null

  const isQuota = error.code === 'QUOTA_EXHAUSTED'
  // The user/tenant has their OWN LLM configuration but its credential is
  // missing. Remediation is finishing that config in Settings → LLM (connect
  // the subscription / add the key) — NOT an inline platform key or a plan
  // upgrade, so we show the exact server message and a direct Settings action.
  const isConfigCred = error.code === 'CONFIG_NO_CREDENTIAL'
  // A stored key exists but can't be decrypted (e.g. ENCRYPTION_KEY rotated).
  // Same remediation shape as isConfigCred — send them to Settings → LLM to
  // re-enter it; an "upgrade your plan" prompt would be nonsensical here.
  const isKeyDecrypt = error.code === 'KEY_DECRYPT_FAILED'
  // The INSTANCE has no platform AI provider/key at all. Nothing the user can
  // buy fixes that — every plan on this instance falls back to the same
  // unconfigured platform default — so the upgrade button is withheld and the
  // server's message (which names who can fix it) is shown instead of the
  // "upgrade to a plan that includes platform AI" blurb.
  const isPlatformUnset = error.code === 'PLATFORM_NOT_CONFIGURED'
  // Both codes point the user at Settings → LLM rather than upgrade/inline key.
  const needsSettings = isConfigCred || isKeyDecrypt
  // A paid plan with an empty wallet. The remedy is buying credits, not
  // upgrading — a Pro user told to "upgrade" when they simply need a top-up
  // has been sent to the wrong page.
  const isCredits = error.code === 'CREDITS_EXHAUSTED'
  // Out of credits, or (credit billing off) out of the monthly allowance: the
  // same three ways forward either way — see OutOfCreditsOptions.
  const isOutOfAi = isCredits || isQuota
  const isAppLimit = error.code === 'PLAN_APP_LIMIT'
  const isDailyTurns = error.code === 'DAILY_TURNS_EXHAUSTED'
  const isLapsed = error.code === 'SUBSCRIPTION_LAPSED'
  // A plan ceiling (records/tables/screens, applications, seats, or a feature
  // this plan doesn't include). Upgrading is the only remedy.
  const isPlanCeiling =
    error.code === 'LIMIT_EXCEEDED' || error.code === 'PLAN_APP_LIMIT' ||
    error.code === 'PLAN_SEAT_LIMIT' || error.code === 'PLAN_REQUIRED' ||
    isDailyTurns

  const title = isKeyDecrypt
    ? 'Re-enter your API key'
    : isPlatformUnset
      ? 'Platform AI isn’t configured'
    : isConfigCred
      ? 'Finish setting up your AI'
      : isCredits
        ? "You're out of AI credits"
        : isLapsed
          ? 'Your subscription has lapsed'
          : isAppLimit
            ? `You've reached your plan's app limit`
          : isDailyTurns
            ? "You've used today's agent turns"
          : isPlanCeiling
            ? 'Your plan limit has been reached'
            : isQuota
              ? "You've used this month's AI allowance"
              : 'AI isn’t set up yet'
  const defaultBlurb = isQuota
    ? 'Your monthly platform AI allowance is used up. Upgrade your plan for a higher limit, or add your own API key to keep going right away.'
    : 'To use AI features, add your own API key, or upgrade to a plan that includes platform AI.'
  // Prefer the specific server message — for credit, lapse and limit refusals
  // it names the exact number or ceiling that was hit, which no generic blurb
  // can. (e.g. "… runs on your Claude subscription, but no Claude account is
  // connected …", or "This application is over its Basic plan limits (records
  // 12,400 of 10,000)").
  // isQuota included: the server names the cap that tripped and its numbers,
  // which the generic blurb ("your monthly platform AI allowance is used up")
  // cannot, and which is the whole point when three different caps can be the
  // cause. Falls back to the blurb if an older server sent no detail.
  const usesServerDetail =
    needsSettings || isCredits || isLapsed || isPlanCeiling || isPlatformUnset || isQuota
  const blurb = (usesServerDetail && error.detail) ? error.detail : defaultBlurb

  const closeAll = () => {
    setMode('choose')
    setApiKey('')
    setSaving(false)
    reset()
  }

  const handleCancel = () => {
    error.cancel?.()
    closeAll()
  }

  const handleUpgrade = () => {
    // Upgrading is a multi-step flow; release the held request and route to
    // the plans page. The user re-triggers their action after upgrading.
    error.cancel?.()
    closeAll()
    navigate('/payments/plans')
  }

  // The fix is in (credits bought, own key or free model set up): carry the
  // refused work on. `aiChanged` = the AI configuration changed, so docks must
  // re-resolve their engine (ModelSettingsModal's event does exactly that).
  const continueWork = (message: string, aiChanged: boolean) => {
    const { retry, resend } = error
    toast.success(message)
    closeAll()
    if (retry) {
      retry()
      if (aiChanged) window.dispatchEvent(new Event('twasta:settings-changed'))
      return
    }
    if (resend) setPendingResend(resend)
    // Remount the dock either way: a resend is picked up by the dock that
    // mounts next (on the new engine, if it changed).
    if (resend || aiChanged) window.dispatchEvent(new Event('twasta:settings-changed'))
  }

  const handleManageApps = () => {
    error.cancel?.()
    closeAll()
    setProjectModalOpen(true)
  }

  const handleRenew = () => {
    error.cancel?.()
    closeAll()
    navigate('/billing')
  }

  const handleOpenSettings = () => {
    // The user's own config is missing its credential — send them to
    // Settings → LLM to connect the account / add the key.
    error.cancel?.()
    closeAll()
    navigate('/settings')
  }

  const handleSaveKey = async () => {
    if (!apiKey.trim()) {
      toast.error('Enter your API key')
      return
    }
    setSaving(true)
    try {
      if (provider === 'anthropic' || provider === 'openai') {
        // A full AI configuration (agent + key) so the Ask-AI dock and Build
        // run on it too, not only direct API calls.
        await quickSetupLlm({ kind: 'own_key', provider, api_key: apiKey.trim() })
      } else {
        await saveSettings({
          llm_provider: provider,
          api_key: apiKey.trim(),
          api_key_storage: 'save_securely',
        } as never)
      }
      continueWork('API key saved — continuing…', true)
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to save API key'
      toast.error(msg)
      setSaving(false)
    }
  }

  return (
    <div className="overlay" onClick={(e) => { if (e.target === e.currentTarget) handleCancel() }}>
      <div className="modal animate-slide-up" style={{ maxWidth: 480, width: '95%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Sparkles size={18} style={{ color: 'var(--color-accent)' }} />
            {title}
          </h3>
          <button className="modal-close" onClick={handleCancel} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9375rem', marginBottom: '1rem', lineHeight: 1.5 }}>
          {blurb}
        </p>

        {isQuota && !error.options && (error.used != null || error.limit != null) && (
          <div
            style={{
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)',
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '0.5rem 0.75rem',
              marginBottom: '1rem',
            }}
          >
            {error.dimension ? (
              // The cap that actually tripped. Showing the combined token pair
              // regardless of cause is how a spend refusal came to display a
              // number well under its own limit.
              <>Used <strong>{error.dimension_unit === 'credits'
                ? formatCredits(error.dimension_used)
                : fmt(error.dimension_used)}</strong> of <strong>{error.dimension_unit === 'credits'
                ? formatCredits(error.dimension_limit)
                : fmt(error.dimension_limit)}</strong> monthly {error.dimension_unit === 'credits'
                ? 'credits' : error.dimension}</>
            ) : (
              <>Used <strong>{fmt(error.used)}</strong> of <strong>{fmt(error.limit)}</strong> monthly tokens</>
            )}
            {error.plan_label || error.plan ? ` (${error.plan_label || error.plan} plan)` : ''}.
          </div>
        )}

        {isDailyTurns && error.limit != null && (
          <div
            style={{
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)',
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '0.5rem 0.75rem',
              marginBottom: '1rem',
            }}
          >
            Used <strong>{fmt(error.used ?? error.limit)}</strong> of <strong>{fmt(error.limit)}</strong>
            {error.turn_kind === 'own' ? ' own-AI' : error.turn_kind === 'credit' ? ' AI-credit' : ''} turns today
            {error.plan_label ? ` on the ${error.plan_label} plan` : ''}
            {error.resets_at ? <> · back at <strong>{formatResetTime(error.resets_at)}</strong></> : null}.
          </div>
        )}

        {(isCredits || (isPlanCeiling && !isDailyTurns)) && (error.balance != null || error.required != null || error.current != null) && (
          <div
            style={{
              fontSize: '0.875rem',
              color: 'var(--color-text-secondary)',
              background: 'var(--color-bg-secondary)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)',
              padding: '0.5rem 0.75rem',
              marginBottom: '1rem',
            }}
          >
            {isCredits ? (
              <>Balance <strong>{formatCredits(error.balance)}</strong> credits
              {error.required != null ? <> · <strong>{formatCredits(error.required)}</strong> needed</> : null}</>
            ) : (
              <>Currently <strong>{fmt(error.current)}</strong> of a <strong>{fmt(error.limit)}</strong> limit
              {error.plan_label ? ` on the ${error.plan_label} plan` : ''}.</>
            )}
          </div>
        )}

        {isOutOfAi && (isCredits || error.options) ? (
          // Any user can carry on by themselves: a pack lands in their OWN
          // wallet (which pays whenever the organization's is empty), their own
          // key, or a free model. An older server that sent no `options` for a
          // monthly allowance falls through to the upgrade/key choice below.
          <OutOfCreditsOptions error={error} onContinue={continueWork} onCancel={handleCancel} />
        ) : isLapsed ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <button className="btn btn-primary" style={{ justifyContent: 'flex-start', gap: '0.5rem' }} onClick={handleRenew}>
              <RefreshCw size={18} /> Renew my subscription
            </button>
            <button className="btn btn-secondary" style={{ justifyContent: 'flex-start', gap: '0.5rem' }} onClick={handleOpenSettings}>
              <KeyRound size={18} /> Use my own API key instead
            </button>
            <button className="btn btn-secondary" onClick={handleCancel}>Not now</button>
          </div>
        ) : isPlanCeiling ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {isAppLimit && (
              <button className="btn btn-primary" style={{ justifyContent: 'flex-start', gap: '0.5rem' }} onClick={handleManageApps}>
                <FolderOpen size={18} /> Manage my apps
              </button>
            )}
            <button className={isAppLimit ? 'btn btn-secondary' : 'btn btn-primary'} style={{ justifyContent: 'flex-start', gap: '0.5rem' }} onClick={handleUpgrade}>
              <ArrowUpCircle size={18} /> See plans
            </button>
            <button className="btn btn-secondary" onClick={handleCancel}>Close</button>
          </div>
        ) : needsSettings ? (
          // Own config missing its credential, or a stored key that no longer
          // decrypts: the only sensible fix is Settings → LLM (an inline
          // platform key or plan upgrade would not satisfy their named config).
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            <button className="btn btn-primary" style={{ justifyContent: 'flex-start', gap: '0.5rem' }} onClick={handleOpenSettings}>
              <KeyRound size={18} /> Open Settings → LLM
            </button>
            <button className="btn btn-secondary" onClick={handleCancel}>
              Not now
            </button>
          </div>
        ) : mode === 'choose' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
            {!isPlatformUnset && (
              <button className="btn btn-primary" style={{ justifyContent: 'flex-start', gap: '0.5rem' }} onClick={handleUpgrade}>
                <ArrowUpCircle size={18} /> Upgrade my plan
              </button>
            )}
            <button
              className={isPlatformUnset ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ justifyContent: 'flex-start', gap: '0.5rem' }}
              onClick={() => setMode('key')}
            >
              <KeyRound size={18} /> Add my own API key
            </button>
            {isPlatformUnset && (
              <button className="btn btn-secondary" onClick={handleCancel}>Not now</button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              Provider
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value as Provider)}
                style={{ marginTop: '0.25rem', width: '100%' }}
              >
                {PROVIDERS.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </label>
            <label style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
              API key
              <input
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Paste your API key"
                autoFocus
                style={{ marginTop: '0.25rem', width: '100%' }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveKey() }}
              />
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
              <button className="btn btn-secondary" onClick={() => setMode('choose')} disabled={saving}>Back</button>
              <button className="btn btn-primary" onClick={handleSaveKey} disabled={saving}>
                {saving ? 'Saving…' : 'Save & continue'}
              </button>
            </div>
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', margin: 0 }}>
              Your key is stored encrypted and used only for your account.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
